import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Grid,
  List,
  FileText,
  Users,
  AlertCircle,
  Clock,
  Activity,
  Flag,
  CheckCircle,
  RefreshCw,
  Calendar,
  Trash2,
  X,
  ArrowLeft,
  TrendingUp
} from 'react-feather';
import { Button } from '../components/ui/button';
import { IconButton } from '../components/ui/IconButton';
import QuickCaptureForm from '../components/PartnerCompanies/QuickCaptureForm';
import PartnerCompanyForm from '../components/PartnerCompanies/PartnerCompanyForm';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import PartnerCompanyCard from '../components/PartnerCompanies/PartnerCompanyCard';
import CompanyDetailView from '../components/PartnerCompanies/CompanyDetailView';
import DashboardMetrics from '../components/PartnerCompanies/DashboardMetrics';
import SearchAndFilters from '../components/PartnerCompanies/SearchAndFilters';
import ProgressAnalytics from '../components/PartnerCompanies/ProgressAnalytics';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePartnerStatusCompat } from '../context/PartnerStatusContext';
import './PartnerCompanies.css';

const PartnerCompanies = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Simple status management (replaces complex statusManager and hardcoded functions)
  const statusContext = usePartnerStatusCompat();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState('');
  const [scrollLoading, setScrollLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('quick'); // 'quick' or 'comprehensive'
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    industry: '',
    assigned_to: '',
    created_by: '',
    search: '',
    willing_to_work: false,
    tech_stack: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: true
  });
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (user?.id) {
      console.log('Filters changed, fetching companies with filters:', filters);
      fetchCompanies();
      fetchMetrics();
      fetchUsers();
    }
  }, [user?.id, filters]);

  // Prevent unnecessary re-renders when companies array changes
  const memoizedCompanies = React.useMemo(() => companies, [companies]);

  // Removed the useEffect that was watching pagination.page since we now handle loading in handleLoadMore

  // Infinite scroll handler with debouncing
  useEffect(() => {
    let timeoutId;
    
    const handleScroll = () => {
      if (scrollLoading || !pagination.hasMore || pageLoading || companies.length === 0) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Load more when user is 200px from bottom
      if (scrollTop + windowHeight >= documentHeight - 200) {
        // Clear any existing timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        // Debounce the scroll event
        timeoutId = setTimeout(() => {
          setScrollLoading(true);
          handleLoadMore();
        }, 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [scrollLoading, pagination.hasMore, pageLoading]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      const params = new URLSearchParams({
        page: 1,
        limit: pagination.limit,
        ...filters
      });

      console.log('Fetching companies with params:', params.toString());
      const response = await api.get(`/api/partner-companies?${params}`);
      console.log('Companies response:', response.data.companies);
      console.log('Pagination data:', response.data.pagination);
      
      const initialCompanies = response.data.companies || [];
      console.log('Initial companies length:', initialCompanies.length);
      
      // Update companies state
      setCompanies(initialCompanies);
      
      // Update pagination state
      setPagination(prev => {
        const hasMore = response.data.pagination.total > initialCompanies.length;
        console.log('Initial pagination calculation:', {
          total: response.data.pagination.total,
          companiesLength: initialCompanies.length,
          hasMore
        });
        return {
          ...prev,
          page: 1,
          total: response.data.pagination.total,
          hasMore
        };
      });
      
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to fetch partner companies');
      // Don't clear companies on error, keep previous state
    } finally {
      setLoading(false);
    }
  };

  // Removed loadMoreCompanies function - now handled in handleLoadMore

  const fetchMetrics = async () => {
    try {
      const response = await api.get('/api/partner-companies/dashboard/metrics');
      setMetrics(response.data);
    } catch (err) {
      console.error('Error fetching metrics:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setFormMode('quick'); // Reset to quick mode
  };

  const handleCompanyCreated = (newCompany) => {
    setCompanies(prev => [newCompany, ...prev]);
    handleCloseForm();
    fetchMetrics(); // Refresh metrics
  };

  const handleAddMoreDetails = () => {
    setFormMode('comprehensive');
  };

  const handleSwitchToQuick = () => {
    setFormMode('quick');
  };

  const handleCompanyUpdated = async (updatedCompany) => {
    try {
      // Fetch the complete company data to ensure proper status structure
      const response = await api.get(`/api/partner-companies/${updatedCompany.id}`);
      const completeCompanyData = response.data;
      
      setCompanies(prev => 
        prev.map(company => 
          company.id === updatedCompany.id ? completeCompanyData : company
        )
      );
    } catch (error) {
      console.error('Error fetching updated company data:', error);
      // Fallback to using the returned data if fetch fails
      setCompanies(prev => 
        prev.map(company => 
          company.id === updatedCompany.id ? updatedCompany : company
        )
      );
    }
    
    setSelectedCompany(null);
    setShowDetailView(false);
    fetchMetrics(); // Refresh metrics
  };

  const handleCompanyDeleted = (companyId) => {
    setCompanies(prev => prev.filter(company => company.id !== companyId));
    setSelectedCompany(null);
    setShowDetailView(false);
    fetchMetrics(); // Refresh metrics
  };

  const handleStatusUpdate = async (companyId, newStatus) => {
    try {
      await api.patch(`/api/partner-companies/${companyId}/status`, {
        status: newStatus
      });
      
      // Update local state
      setCompanies(prev => 
        prev.map(company => 
          company.id === companyId 
            ? { ...company, partner_status: { current_status: newStatus } }
            : company
        )
      );
      
      fetchMetrics(); // Refresh metrics
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update company status');
    }
  };

  const handleContactLogged = async (companyId, contactAttempt) => {
    try {
      console.log('Contact logged for company:', companyId, contactAttempt);
      
      setError(''); // Clear any existing errors
      
      // Refresh the company data to get latest recent_actions and latest_follow_up
      const response = await api.get(`/api/partner-companies/${companyId}`);
      const updatedCompany = response.data;
      
      // Update the company in the companies list with fresh data
      setCompanies(prevCompanies => 
        prevCompanies.map(company => 
          company.id === companyId 
            ? updatedCompany
            : company
        )
      );
      
      // Also update communication_logs count
      setCompanies(prevCompanies => 
        prevCompanies.map(company => 
          company.id === companyId 
            ? { 
                ...company, 
                communication_logs: (company.communication_logs || 0) + 1
              }
            : company
        )
      );
    } catch (error) {
      console.error('Error handling contact log:', error);
      setError('Failed to update contact information');
    }
  };

  const handleFiltersChange = (newFilters) => {
    console.log('Filter change detected:', { old: filters, new: newFilters });
    
    // Check if filters actually changed
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(newFilters);
    
    if (filtersChanged) {
      setFilters(newFilters);
      setPagination(prev => ({ ...prev, page: 1, hasMore: true })); // Reset to first page
    }
  };

  const handleLoadMore = async () => {
    if (pageLoading || scrollLoading) return;
    
    try {
      setPageLoading(true);
      const params = new URLSearchParams({
        page: pagination.page + 1,
        limit: pagination.limit,
        ...filters
      });

      const response = await api.get(`/api/partner-companies?${params}`);
      console.log('Load more response:', response.data.companies);
      console.log('Load more pagination:', response.data.pagination);
      
      const newCompanies = response.data.companies || [];
      setCompanies(prev => {
        const updatedCompanies = [...prev, ...newCompanies];
        console.log('Total companies after load more:', updatedCompanies.length);
        return updatedCompanies;
      });
      setPagination(prev => {
        // Use the actual total companies loaded (prev.length + newCompanies.length)
        const totalLoaded = prev.page * prev.limit + newCompanies.length;
        console.log('Pagination calculation:', {
          prevPage: prev.page,
          limit: prev.limit,
          newCompaniesLength: newCompanies.length,
          totalLoaded,
          apiTotal: response.data.pagination.total,
          hasMore: totalLoaded < response.data.pagination.total
        });
        return {
          ...prev,
          page: prev.page + 1,
          total: response.data.pagination.total,
          hasMore: totalLoaded < response.data.pagination.total
        };
      });
    } catch (err) {
      setError('Failed to load more companies');
      console.error('Error loading more companies:', err);
    } finally {
      setPageLoading(false);
      setScrollLoading(false);
    }
  };

  // Status functions are now provided by the statusManager hook
  // These are kept for backward compatibility during migration
  const getStatusColor = statusContext.getStatusColor;
  const getStatusIcon = (status) => {
    const iconName = statusContext.getStatusIcon(status);
    // Import the icon dynamically based on the name
    const iconComponents = {
      'FileText': <FileText />,
      'Users': <Users />,
      'AlertCircle': <AlertCircle />,
      'Clock': <Clock />,
      'Activity': <Activity />,
      'Flag': <Flag />,
      'CheckCircle': <CheckCircle />,
      'RefreshCw': <RefreshCw />,
      'Calendar': <Calendar />,
      'Trash2': <Trash2 />,
      'X': <X />,
      'ArrowLeft': <ArrowLeft />
    };
    return iconComponents[iconName] || <FileText />;
  };
  const formatStatus = statusContext.formatStatus;

  return (
    <DefaultPageLayout>
      <div className="partner-companies-container">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            {/* <Button
              variant="neutral-tertiary"
              icon={<TrendingUp />}
              onClick={() => navigate("/thehub")}
            >
              Back
            </Button> */}
            <div className="flex items-center gap-4">
              
              <div>
                <h1 className="text-heading-2">Partner Companies</h1>
                <p className="text-body-2 text-neutral-600">
                  Manage partner company onboarding and relationships
                </p>
              </div>
              <IconButton
                icon={<TrendingUp />}
                variant="brand-primary"
                size="lg"
                onClick={() => setShowAnalytics(true)}
                title="View Progress Analytics"
              />
            </div>
          </div>
          <div className="header-right">
            <Button
              variant="brand-primary"
              icon={<Plus />}
              onClick={() => setShowForm(true)}
            >
              Add Company
            </Button>
          </div>
        </div>

        {/* Dashboard Metrics */}
        {/* {metrics && (
          <DashboardMetrics metrics={metrics} />
        )} */}

        {/* Search and Filters */}
        <SearchAndFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          users={users}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          metrics={metrics}
        />

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <Button
              variant="neutral-tertiary"
              size="sm"
              onClick={() => setError('')}
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* Content Area - Always rendered, content changes based on state */}
        {loading && companies.length === 0 && !error ? (
          // Initial loading state
          <div className="loading-container">
            {/* BunnyLoader is not defined in the original file, assuming it's a placeholder */}
            <p>Loading partner companies...</p>
          </div>
        ) : companies.length === 0 && !loading ? (
          // Empty state when no companies found
          <div className="empty-state">
            <IconButton
              icon={<TrendingUp />}
              variant="neutral-tertiary"
              size="xl"
            />
            <h3>No Partner Companies Found</h3>
            <p>Get started by adding your first partner company</p>
            <Button
              variant="brand-primary"
              icon={<Plus />}
              onClick={() => setShowForm(true)}
            >
              Add First Company
            </Button>
          </div>
        ) : (
          // Companies grid/list view
          <>
            <div className={`companies-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
              {memoizedCompanies.map(company => (
                <PartnerCompanyCard
                  key={company.id}
                  company={company}
                  onEdit={() => setSelectedCompany(company)}
                  onView={() => {
                    setSelectedCompany(company);
                    setShowDetailView(true);
                  }}
                  onDelete={handleCompanyDeleted}
                  onStatusUpdate={handleStatusUpdate}
                  onContactLogged={handleContactLogged}
                                getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              statusManager={statusContext}
                  formatStatus={formatStatus}
                  viewMode={viewMode}
                />
              ))}
              {pageLoading && (
                <div className="page-loading">
                  <div className="loading-content">
                    {/* BunnyLoader is not defined in the original file, assuming it's a placeholder */}
                    <p>Loading more companies...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Infinite Scroll Info */}
            {pagination.total > 0 && (
              <div className="infinite-scroll-info">
                <div className="scroll-info">
                  Showing {companies.length} of {pagination.total} companies
                  {pagination.hasMore && (
                    <span className="scroll-hint">
                      • Scroll down to load more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Load More Button */}
            {pagination.hasMore && !pageLoading && (
              <div className="load-more-container">
                <Button
                  variant="neutral-tertiary"
                  onClick={handleLoadMore}
                  disabled={pageLoading}
                >
                  Load More Companies
                </Button>
              </div>
            )}
          </>
        )}

        {/* Company Form Modal */}
        {showForm && (
          <div 
            className="modal-overlay" 
            onClick={(e) => {
              // Only close if clicking on the overlay, not the modal content
              if (e.target === e.currentTarget) {
                handleCloseForm();
              }
            }}
            onKeyDown={(e) => {
              // Prevent any keyboard events from bubbling up
              e.stopPropagation();
            }}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              {formMode === 'quick' ? (
                <QuickCaptureForm
                  onSuccess={handleCompanyCreated}
                  onCancel={handleCloseForm}
                  onAddMoreDetails={handleAddMoreDetails}
                  users={users}
                />
              ) : (
                <PartnerCompanyForm
                  onSuccess={handleCompanyCreated}
                  onCancel={handleCloseForm}
                  onSwitchToQuick={handleSwitchToQuick}
                  users={users}
                />
              )}
            </div>
          </div>
        )}

                        {/* Edit Company Modal */}
                {selectedCompany && !showDetailView && (
                  <div 
                    className="modal-overlay" 
                    onClick={(e) => {
                      // Only close if clicking on the overlay, not the modal content
                      if (e.target === e.currentTarget) {
                        setSelectedCompany(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent any keyboard events from bubbling up
                      e.stopPropagation();
                    }}
                  >
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                      <PartnerCompanyForm
                        company={selectedCompany}
                        onSuccess={handleCompanyUpdated}
                        onCancel={() => setSelectedCompany(null)}
                        users={users}
                      />
                    </div>
                  </div>
                )}

                {/* Company Detail View Modal */}
                {selectedCompany && showDetailView && (
                  <CompanyDetailView
                    company={selectedCompany}
                    onClose={() => {
                      setShowDetailView(false);
                      setSelectedCompany(null);
                    }}
                    onEdit={() => setShowDetailView(false)}
                    onDelete={handleCompanyDeleted}
                    onStatusUpdate={handleStatusUpdate}
                    users={users}
                  />
                )}

                {/* Progress Analytics Modal */}
                {showAnalytics && (
                  <ProgressAnalytics
                    onClose={() => setShowAnalytics(false)}
                    metrics={metrics}
                  />
                )}
      </div>
    </DefaultPageLayout>
  );
};

export default PartnerCompanies; 